'use client';
import Link from 'next/link';
import Navbar from '@/components/ui/Navbar';
import Footer from '@/components/ui/Footer';

const values = [
  { icon: '🔍', title: 'Transparency', desc: 'Every product listing includes honest condition notes, refurbishment grade, and full specifications.' },
  { icon: '♻️', title: 'Sustainability', desc: 'Refurbished printers reduce e-waste and give quality hardware a second life.' },
  { icon: '💬', title: 'Buyer Support', desc: 'We answer questions before you buy — helping you choose the right printer for your needs.' },
  { icon: '✅', title: 'Quality Assurance', desc: 'All printers are professionally inspected and tested before listing.' },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#030812] text-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="border-b border-white/10 bg-[linear-gradient(135deg,#07111f_0%,#0f1f3d_100%)] py-16">
          <div className="container mx-auto max-w-6xl px-4">
            <nav className="mb-6 flex items-center gap-2 text-sm text-slate-400">
              <Link href="/" className="hover:text-white transition">Home</Link>
              <span>/</span>
              <span className="text-white">About</span>
            </nav>
            <h1 className="text-3xl font-black text-white md:text-4xl">About RePrint Solution</h1>
            <p className="mt-3 max-w-2xl text-slate-300">
              We help homes and offices find professionally checked refurbished printers with clear model details, honest condition grades, and genuine buyer support.
            </p>
          </div>
        </section>

        {/* Story */}
        <section className="py-14">
          <div className="container mx-auto max-w-4xl px-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 md:p-12">
              <h2 className="text-2xl font-black text-white">Our Story</h2>
              <div className="mt-5 space-y-4 text-sm leading-7 text-slate-300">
                <p>
                  RePrint Solution was founded with a simple idea: buying a refurbished printer should be straightforward, honest, and reliable. Too often, buyers encounter vague condition descriptions, hidden costs, and poor after-sale support.
                </p>
                <p>
                  We set out to change that. Every printer on our platform is professionally inspected, graded with a clear condition rating, and listed with full specifications so you know exactly what you are getting before you buy.
                </p>
                <p>
                  We serve home users looking for affordable everyday printing and offices that need dependable, high-volume devices — all at a fraction of the cost of buying new.
                </p>
                <p>
                  RePrint Solution is an independent refurbished printer retailer and is not affiliated with, sponsored by, or endorsed by any printer manufacturer or brand. All brand names and trademarks are the property of their respective owners.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="border-t border-white/10 py-14">
          <div className="container mx-auto max-w-6xl px-4">
            <h2 className="text-2xl font-black text-white">What We Stand For</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {values?.map((v) => (
                <div key={v?.title} className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                  <span className="text-3xl">{v?.icon}</span>
                  <h3 className="mt-3 font-bold text-white">{v?.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-300">{v?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-white/10 py-14">
          <div className="container mx-auto max-w-3xl px-4 text-center">
            <h2 className="text-2xl font-black text-white">Ready to find your printer?</h2>
            <p className="mt-3 text-slate-300">Browse our full range of professionally refurbished printers.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <Link href="/home-printers" className="rounded-lg bg-violet-600 px-6 py-3 text-sm font-bold text-white transition hover:bg-violet-500">
                Home Printers
              </Link>
              <Link href="/office-printers" className="rounded-lg border border-white/20 bg-white/10 px-6 py-3 text-sm font-bold text-white transition hover:bg-white/20">
                Office Printers
              </Link>
              <Link href="/contact" className="rounded-lg border border-white/20 bg-white/10 px-6 py-3 text-sm font-bold text-white transition hover:bg-white/20">
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
